import React from "react";
import {
  CheckCircle2,
  Building2,
  Calendar,
  MapPin,
  Image as ImageIcon,
  FileText,
  Users,
  Gift,
  ArrowLeft,
  Send,
  Ticket,
  Banknote,
  Layers,
} from "lucide-react";
import { toast } from "react-toastify";
import { formatPrice, getTicketCurrency } from "../../../../../utils/currency";

/**
 * Step Four: Review Component
 * Review all information before submission
 */
const StepFourReview = ({ formData, onBack, onSubmit, eventType }) => {
  const handleSubmit = () => {
    // Final validation
    if (!formData.organizer) {
      toast.error("Please select an organizer");
      return;
    }
    if (!formData.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }
    if (!formData.eventStartDate) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.eventEndDate) {
      toast.error("End date is required");
      return;
    }
    if (!formData.venueName) {
      toast.error("Venue name is required");
      return;
    }

    // Trade Mission specific validation
    if (eventType === "trade_mission") {
      if (!formData.totalParticipantSlots || parseInt(formData.totalParticipantSlots) <= 0) {
        toast.error("Total participant slots is required");
        return;
      }
    }

    // Conference specific validation
    if (eventType === "conference") {
      if (!formData.overallSlots || parseInt(formData.overallSlots) <= 0) {
        toast.error("Overall slots is required");
        return;
      }
      if (!formData.ticketTypes || formData.ticketTypes.length === 0) {
        toast.error("At least one ticket type is required");
        return;
      }
    }

    onSubmit(formData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "Not specified";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const typeLabel = eventType === "conference" ? "Conference" : "Trade Mission";

  const getQuestionTypeLabel = (type) => {
    const labels = {
      short_text: "Short Text",
      long_text: "Long Text",
      single_choice: "Single Choice",
      multiple_choice: "Multiple Choice",
      dropdown: "Dropdown",
      date: "Date",
      signature: "Signature",
      free_text: "Free Text",
    };
    return labels[type] || type || "—";
  };

  const isNewQuestionsFormat = (questions) =>
    questions?.length > 0 && questions[0]?.sectionTitle !== undefined;

  const totalQuestionsCount = (questions) => {
    if (!questions?.length) return 0;
    if (isNewQuestionsFormat(questions)) {
      return questions.reduce((sum, s) => sum + (s.questions?.length || 0), 0);
    }
    return questions.length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Review {typeLabel} Details
            </h2>
            <p className="mt-0.5 text-xs text-gray-600 sm:text-sm">
              Review all information before submitting your {typeLabel.toLowerCase()}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Organizer Section */}
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center mb-4 space-x-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Organizer</h3>
            </div>
            {formData.organizer ? (
              <div className="flex items-center space-x-3">
                {formData.organizer.logo && (
                  <img
                    src={formData.organizer.logo}
                    alt={formData.organizer.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <p className="font-semibold text-gray-900">
                    {formData.organizer.name}
                  </p>
                  {formData.organizer.email && (
                    <p className="text-sm text-gray-600">
                      {formData.organizer.email}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No organizer selected</p>
            )}
          </div>

          {/* Venue Information */}
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center mb-4 space-x-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Venue Information</h3>
            </div>
            <div className="space-y-3">
              {/* Event Title */}
              {formData.eventName && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Event Title</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formData.eventName}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500">Start Date</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatDate(formData.eventStartDate || formData.eventDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">End Date</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatDate(formData.eventEndDate || formData.eventStartDate || formData.eventDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Start Time</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatTime(formData.eventStartTime || formData.eventTime)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">End Time</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formatTime(formData.eventEndTime || formData.eventStartTime || formData.eventTime)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Venue Name</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {formData.venueName || "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Address</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {formData.venueStreetAddress || "Not specified"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500">City</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formData.city || "Not specified"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">Country</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {formData.country || "Not specified"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trade Mission: Participant Slots */}
          {eventType === "trade_mission" && formData.totalParticipantSlots && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Users className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">Participant Slots</h3>
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {formData.totalParticipantSlots} slots
              </p>
            </div>
          )}

          {/* Conference: Registration Form / Motivational Questions */}
          {eventType === "conference" &&
            formData.motivationalQuestions &&
            formData.motivationalQuestions.length > 0 && (
              <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center mb-4 space-x-2">
                  <Layers className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">
                    Registration Form ({totalQuestionsCount(formData.motivationalQuestions)} questions
                    {isNewQuestionsFormat(formData.motivationalQuestions) &&
                      ` in ${formData.motivationalQuestions.length} section${formData.motivationalQuestions.length === 1 ? "" : "s"}`}
                  </h3>
                </div>

                {isNewQuestionsFormat(formData.motivationalQuestions) ? (
                  <div className="space-y-4">
                    {formData.motivationalQuestions.map((section, sectionIdx) => (
                      <div key={sectionIdx} className="rounded-lg border border-purple-200 bg-purple-50/50 overflow-hidden">
                        <div className="px-3 py-2 border-b border-purple-200 bg-purple-100/70">
                          <p className="text-sm font-bold text-gray-900 uppercase">
                            {section.sectionTitle || `Section ${sectionIdx + 1}`}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {section.questions?.length || 0} question{(section.questions?.length || 0) === 1 ? "" : "s"}
                          </p>
                        </div>
                        <div className="p-3 space-y-2">
                          {section.questions?.map((q, qIdx) => (
                            <div key={qIdx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <p className="text-sm font-semibold text-gray-900">
                                {qIdx + 1}. {q.question}
                                {q.required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">
                                Type: {getQuestionTypeLabel(q.type)}
                              </p>
                              {q.options && q.options.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {q.options.map((opt, optIdx) => (
                                    <p key={optIdx} className="text-xs text-gray-600">
                                      • {opt}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.motivationalQuestions.map((q, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900">
                          {idx + 1}. {q.question}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Type: {getQuestionTypeLabel(q.type) || (q.type === "multiple_choice" ? "Multiple Choice" : "Free Text")}
                        </p>
                        {q.options && q.options.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {q.options.map((opt, optIdx) => (
                              <p key={optIdx} className="text-xs text-gray-600">
                                • {opt}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          {/* Conference: Tickets */}
          {eventType === "conference" && formData.overallSlots && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Ticket className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Conference Tickets
                </h3>
              </div>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Overall Slots:</span>
                  <span className="text-lg font-bold text-blue-600">{formData.overallSlots}</span>
                </div>
              </div>
              {formData.ticketTypes && formData.ticketTypes.length > 0 ? (
                <div className="space-y-3">
                  {formData.ticketTypes.map((ticket, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full">
                            <Ticket className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-gray-900">
                              {ticket.name}
                            </h4>
                            <div className="flex items-center space-x-3 mt-1">
                              <div className="flex items-center space-x-1 text-sm font-semibold text-blue-700">
                                <Banknote className="w-4 h-4" />
                                <span>{formatPrice(ticket.price || 0, getTicketCurrency(ticket))}</span>
                              </div>
                              <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded">
                                {ticket.slotsAllocated} slots
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {ticket.description && (
                        <p className="mt-2 text-sm text-gray-700">{ticket.description}</p>
                      )}
                      {ticket.whatsIncluded && Array.isArray(ticket.whatsIncluded) && ticket.whatsIncluded.length > 0 && (
                        <div className="mt-3">
                          <p className="mb-2 text-xs font-semibold text-gray-700">What's Included:</p>
                          <div className="flex flex-wrap gap-2">
                            {ticket.whatsIncluded.map((item, itemIdx) => (
                              <div
                                key={itemIdx}
                                className="flex items-center space-x-1.5 px-2.5 py-1 bg-white border border-blue-300 rounded-md"
                              >
                                <Gift className="w-3 h-3 text-blue-600" />
                                <span className="text-xs text-gray-700">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No ticket types added</p>
              )}
            </div>
          )}

          {/* About Event */}
          {formData.aboutEvent && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">About</h3>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {formData.aboutEvent}
              </p>
            </div>
          )}

          {/* Itinerary */}
          {formData.itinerary && formData.itinerary.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-4 space-x-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Itinerary ({formData.itinerary.length} items)
                </h3>
              </div>
              <div className="space-y-3">
                {formData.itinerary.map((item, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-600">{item.time}</p>
                    </div>
                    <p className="text-xs text-gray-700">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Thumbnail */}
          <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <h3 className="mb-3 text-sm font-bold text-gray-900">Thumbnail</h3>
            {formData.thumbnail ? (
              <img
                src={formData.thumbnail}
                alt="Thumbnail"
                className="w-full rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center h-32 bg-gray-100 rounded-lg">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>

          {/* Featured Images */}
          {formData.featuredImages && formData.featuredImages.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <h3 className="mb-3 text-sm font-bold text-gray-900">
                Featured Images ({formData.featuredImages.length})
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {formData.featuredImages.slice(0, 4).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Featured ${idx + 1}`}
                    className="w-full rounded-lg"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Speakers */}
          {formData.performers && formData.performers.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-3 space-x-2">
                <Users className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-bold text-gray-900">
                  Speakers ({formData.performers.length})
                </h3>
              </div>
              <div className="space-y-1">
                {formData.performers.map((performer, idx) => {
                  const name = typeof performer === "string" ? performer : performer?.name;
                  return (
                    <p key={idx} className="text-xs text-gray-700">
                      • {name}
                    </p>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sponsors */}
          {formData.sponsors && formData.sponsors.length > 0 && (
            <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center mb-3 space-x-2">
                <Gift className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-bold text-gray-900">
                  Sponsors ({formData.sponsors.length})
                </h3>
              </div>
              <div className="space-y-2">
                {formData.sponsors.map((sponsor, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    {sponsor.logo && (
                      <img
                        src={sponsor.logo}
                        alt={sponsor.name}
                        className="w-8 h-8 rounded object-cover"
                      />
                    )}
                    <p className="text-xs font-semibold text-gray-900">
                      {sponsor.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onBack}
          className="flex items-center px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSubmit}
          className="flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-sm transition-all hover:shadow-md"
        >
          <Send className="mr-2 w-4 h-4" />
          Submit {typeLabel}
        </button>
      </div>
    </div>
  );
};

export default StepFourReview;

